import type { Recommendation, SignalKey, SignalState } from '../types/intelligence'
import type { Listing } from '../types/housing'
import type { Programme } from '../types/admissions'
import type { KnowledgeResource, ServiceProvider } from '../types/marketplace'
import type { CommunityGroup } from '../types/community'
import type { Opportunity } from '../types/campus'
import { listings } from '../data/housing'
import { programmes } from '../data/admissions'
import { knowledgeResources, serviceProviders } from '../data/marketplace'
import { groups } from '../data/community'
import { careerProfile, opportunities } from '../data/career'
import { defaultPreferences } from '../data/roommates'
import { courses } from '../data/academics'
import { naira } from './format'

export const DEFAULT_SIGNALS: SignalState = {
  search_history: true,
  saved_items: true,
  declared_preferences: true,
  course_enrolment: true,
  marketplace_activity: true,
}

function enabled(signals: SignalState, key: SignalKey): boolean {
  return signals[key]
}

function rank<T>(items: Recommendation<T>[], limit: number): Recommendation<T>[] {
  return [...items].sort((a, b) => b.score - a.score).slice(0, limit)
}

/**
 * Listings similar to the one being viewed. Personalized against the declared
 * budget when that signal is available, otherwise ranked by rating alone.
 */
export function recommendListings(
  current: Listing,
  signals: SignalState,
  limit = 3,
): Recommendation<Listing>[] {
  const personalized = enabled(signals, 'declared_preferences')
  const budget = defaultPreferences.budgetMax

  const scored = listings
    .filter((listing) => listing.id !== current.id)
    .map((listing) => {
      let score = listing.rating * 10
      const reasons: string[] = []

      if (listing.neighbourhood === current.neighbourhood) {
        score += 25
        reasons.push(`also in ${listing.neighbourhood}`)
      }
      if (Math.abs(listing.minutesToCampus - current.minutesToCampus) <= 5) {
        score += 15
        reasons.push(`${listing.minutesToCampus} minutes to campus`)
      }
      if (personalized && listing.rentPerYear <= budget) {
        score += 20
        reasons.push(`within your ${naira(budget, true)} budget`)
      }
      if (listing.verification === 'verified') {
        score += 12
        reasons.push('verified listing')
      }

      const reason = personalized
        ? reasons.slice(0, 2).join(' · ') || 'similar to what you are viewing'
        : `${listing.rating.toFixed(1)} rating from ${listing.reviewCount} reviews`

      return { item: listing, score, reason, fallback: !personalized }
    })

  return rank(scored, limit)
}

/** Programmes close to the ones a student is comparing. */
export function recommendProgrammes(
  reference: Programme | undefined,
  signals: SignalState,
  limit = 3,
): Recommendation<Programme>[] {
  const personalized = enabled(signals, 'saved_items') && Boolean(reference)

  const scored = programmes
    .filter((programme) => programme.id !== reference?.id)
    .map((programme) => {
      let score = 40
      const reasons: string[] = []

      if (reference && programme.field === reference.field) {
        score += 30
        reasons.push(`same field as ${reference.title}`)
      }
      if (reference && programme.level === reference.level) {
        score += 20
        reasons.push(`also ${programme.level.toLowerCase()} level`)
      }
      if (programme.scholarshipIds.length > 0) {
        score += 15
        reasons.push(
          `${programme.scholarshipIds.length} linked scholarship${programme.scholarshipIds.length === 1 ? '' : 's'}`,
        )
      }

      const reason = personalized
        ? reasons.slice(0, 2).join(' · ') || 'related to your shortlist'
        : `${programme.level} · ${programme.deadlineLabel}`

      return { item: programme, score, reason, fallback: !personalized }
    })

  return rank(scored, limit)
}

/** Service providers relevant to a field of study or a programme requirement. */
export function recommendProviders(
  context: { field?: string; need?: string },
  signals: SignalState,
  limit = 3,
): Recommendation<ServiceProvider>[] {
  const personalized = enabled(signals, 'marketplace_activity')

  const scored = serviceProviders.map((provider) => {
    let score = provider.rating * 12
    const reasons: string[] = []

    const haystack = `${provider.headline} ${provider.category} ${provider.subcategory} ${provider.skills.join(' ')}`.toLowerCase()

    if (context.need && haystack.includes(context.need.toLowerCase())) {
      score += 35
      reasons.push(`works on ${context.need.toLowerCase()}`)
    }
    if (context.field && haystack.includes(context.field.toLowerCase())) {
      score += 25
      reasons.push(`experienced in ${context.field}`)
    }
    if (provider.identityVerified) {
      score += 10
      reasons.push('identity verified')
    }

    const reason = personalized
      ? reasons.slice(0, 2).join(' · ') || `${provider.completedOrders} completed orders`
      : `${provider.rating.toFixed(1)} rating · ${provider.completedOrders} orders`

    return { item: provider, score, reason, fallback: !personalized }
  })

  return rank(scored, limit)
}

/** Knowledge resources matched to registered courses. */
export function recommendResources(
  signals: SignalState,
  limit = 3,
): Recommendation<KnowledgeResource>[] {
  const personalized = enabled(signals, 'course_enrolment')
  const registered = courses.filter((course) => course.status === 'registered')

  const scored = knowledgeResources.map((resource) => {
    let score = resource.rating * 10
    const reasons: string[] = []

    const match = registered.find((course) =>
      `${course.title} ${course.code}`.toLowerCase().includes(resource.field.toLowerCase()) ||
      resource.title.toLowerCase().includes(course.title.split(' ')[0].toLowerCase()),
    )
    if (personalized && match) {
      score += 40
      reasons.push(`matches ${match.code}`)
    }
    if (resource.licences.some((licence) => licence.model === 'free')) {
      score += 12
      reasons.push('free access available')
    }
    if (resource.authorVerified) {
      score += 8
      reasons.push('verified author')
    }

    const reason = personalized
      ? reasons.slice(0, 2).join(' · ') || `popular in ${resource.field}`
      : `${resource.rating.toFixed(1)} rating in ${resource.field}`

    return { item: resource, score, reason, fallback: !personalized }
  })

  return rank(scored, limit)
}

/** Roles matched against the declared career profile. */
export function recommendRoles(
  signals: SignalState,
  limit = 3,
): Recommendation<Opportunity>[] {
  const personalized = enabled(signals, 'declared_preferences')
  const skills = careerProfile.skills.map((skill) => skill.toLowerCase())

  const scored = opportunities.map((opportunity) => {
    let score = opportunity.employerVerified ? 30 : 5
    const overlap = opportunity.skills.filter((skill) =>
      skills.some((mine) => skill.toLowerCase().includes(mine) || mine.includes(skill.toLowerCase())),
    )

    if (personalized && overlap.length > 0) {
      score += overlap.length * 25
    }

    const reason = personalized
      ? overlap.length > 0
        ? `matches ${overlap.join(' and ')} on your profile`
        : `open to your programme · ${opportunity.deadlineLabel}`
      : `${opportunity.kind} · ${opportunity.deadlineLabel}`

    return { item: opportunity, score, reason, fallback: !personalized }
  })

  return rank(scored, limit)
}

/** Groups suggested from shared courses and existing membership. */
export function recommendGroups(
  signals: SignalState,
  limit = 3,
): Recommendation<CommunityGroup>[] {
  const personalized = enabled(signals, 'course_enrolment')

  const scored = groups
    .filter((group) => !group.joined)
    .map((group) => {
      let score = Math.min(group.members / 10, 40)
      const reasons: string[] = []

      if (personalized && group.kind === 'Course') {
        score += 40
        reasons.push('tied to a course you are registered for')
      }
      if (group.privacy === 'Open') {
        score += 10
        reasons.push('open to join')
      }

      const reason = personalized
        ? reasons.slice(0, 2).join(' · ') || `${group.members.toLocaleString()} members from your campus`
        : `${group.members.toLocaleString()} members · ${group.kind}`

      return { item: group, score, reason, fallback: !personalized }
    })

  return rank(scored, limit)
}
